import type { EquipeLinha, PeriodoWizard } from "./nova-obra.equipe";
import type { EquipeObraTarget } from "@/modules/recursos/equipe-obra.types";
import type { PapelObra } from "@/modules/recursos/papeis-obra.types";
import type { ConflitoNovaAlocacao, NovaAlocacao } from "@/modules/recursos";

/** Alocações com profissional da Etapa 4, no formato das verificações de Recursos (HU 2059). */
export function novasDaEquipe(
  linhas: readonly EquipeLinha[],
  papeisById: ReadonlyMap<string, PapelObra>,
): NovaAlocacao[] {
  return linhas.flatMap((linha) =>
    linha.usuarioId
      ? [
          {
            id: linha.id,
            usuarioId: linha.usuarioId,
            papelObraId: linha.papelObraId,
            papel: papeisById.get(linha.papelObraId)?.nome,
            inicio: linha.inicio,
            fim: linha.fim,
          },
        ]
      : [],
  );
}

/**
 * A obra dos diálogos de verificação: ainda sem id nem código, citada pela embarcação (como em
 * "Equipe incompleta", Figma 12227:420586) e com o período do wizard.
 */
export function obraDaNovaObra(rotulo: string, periodo: PeriodoWizard): EquipeObraTarget {
  return { id: "", codigo: rotulo, nome: "", inicio: periodo.inicio, fim: periodo.fim };
}

/** Conflitos de alocação de um profissional da equipe com outras obras (pendência da Revisão). */
export interface ConflitosDoProfissional {
  usuarioId: string;
  profissional: string;
  /** Um período por outra obra em conflito: do início da primeira ao fim da última alocação dela. */
  periodos: { inicio: string; fim: string }[];
}

/** Agrupa os conflitos por profissional e, dentro dele, por obra (não por alocação). */
export function conflitosPorProfissional(
  conflitos: readonly ConflitoNovaAlocacao[],
): ConflitosDoProfissional[] {
  const grupos = new Map<string, ConflitosDoProfissional>();
  const periodoDaObra = new Map<string, { inicio: string; fim: string }>();

  for (const { usuarioId, profissional, existente } of conflitos) {
    const grupo = grupos.get(usuarioId) ?? { usuarioId, profissional, periodos: [] };
    grupos.set(usuarioId, grupo);

    const chave = `${usuarioId}|${existente.obra.id}`;
    const periodo = periodoDaObra.get(chave);
    if (periodo) {
      if (existente.inicio < periodo.inicio) periodo.inicio = existente.inicio;
      if (existente.fim > periodo.fim) periodo.fim = existente.fim;
    } else {
      const novo = { inicio: existente.inicio, fim: existente.fim };
      periodoDaObra.set(chave, novo);
      grupo.periodos.push(novo);
    }
  }

  return [...grupos.values()];
}
